import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Search, UserPlus, UserMinus, MoreVertical, MessageSquare } from 'lucide-react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * CONTACTS MANAGEMENT PAGE
 * 
 * Per FINAL DIRECTIVE:
 * - Contacts = all "friends" (private list, never public)
 * - Manage contacts: add, remove, view
 */
export default function Contacts() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    loadContacts();
  }, [user]);

  const loadContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(res.data?.contacts || []);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/users/search?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data?.users || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const addContact = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/contacts/add`, { contact_id: userId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact added');
      loadContacts();
      setSearchResults([]);
      setSearchQuery('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contact');
    }
  };

  const removeContact = async (contactId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact removed');
      loadContacts();
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--lk-sage)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button onClick={() => navigate('/')} className="p-2 -ml-2 rounded-lg hover:opacity-70">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Contacts</h1>
            <span className="text-sm px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
              {contacts.length}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Search */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchUsers(e.target.value);
              }}
              placeholder="Search for people to add..."
              className="flex-1 bg-transparent outline-none"
              style={{ color: 'var(--fy-text-primary)' }}
            />
          </div>
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium" style={{ color: 'var(--fy-text-secondary)' }}>Search Results</p>
              {searchResults.map(user => (
                <div key={user.user_id} className="flex items-center justify-between p-2 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                  <div className="flex items-center gap-3">
                    <img 
                      src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=6B8F71&color=fff&size=40`}
                      alt={user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>{user.name}</p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>@{user.username}</p>
                    </div>
                  </div>
                  <Button onClick={() => addContact(user.user_id)} size="sm" style={{ backgroundColor: 'var(--lk-sage)' }}>
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          {searching && (
            <p className="mt-4 text-sm text-center" style={{ color: 'var(--fy-text-secondary)' }}>Searching...</p>
          )}
        </Card>

        {/* Contacts List */}
        <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
          <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
            <Users className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
            Your Contacts
          </h3>
          
          {contacts.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
              <p className="font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>No contacts yet</p>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Search for people above to add them as contacts.
              </p>
              <p className="text-xs mt-2" style={{ color: 'var(--fy-text-disabled)' }}>
                Your contacts list is always private.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {contacts.map(contact => (
                <div key={contact.contact_id || contact.user_id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                  <div className="flex items-center gap-3">
                    <img 
                      src={contact.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name || 'U')}&background=6B8F71&color=fff&size=40`}
                      alt={contact.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>{contact.name}</p>
                      <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>@{contact.username}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={() => navigate(`/messages?to=${contact.user_id}`)} 
                      variant="outline" 
                      size="sm"
                    >
                      <MessageSquare className="w-4 h-4" />
                    </Button>
                    <Button 
                      onClick={() => removeContact(contact.contact_id || contact.user_id)} 
                      variant="outline" 
                      size="sm"
                      style={{ borderColor: 'var(--lk-terra)', color: 'var(--lk-terra)' }}
                    >
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Privacy Note */}
        <p className="text-xs text-center" style={{ color: 'var(--fy-text-disabled)' }}>
          Your contacts list is always private and never visible to others.
        </p>
      </div>
    </div>
  );
}
